import React, { useEffect, useState } from 'react';
import { Link, Outlet, useLocation } from 'react-router-dom';
import { Menu, Moon, Sun, X } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { cn } from '../../lib/utils';
import { BRAND_GRADIENT } from '../../lib/brand';
import { AuthDialog } from '../auth/AuthDialog';
import { useDesignSystem } from '../../contexts/DesignSystemContext';

interface LandingLayoutProps {
  onLogin: () => void;
  partner?: string;
}

const NAV_ITEMS = [
  { path: '/', label: 'Home' },
  { path: '/how-it-works', label: 'How It Works' },
  { path: '/research', label: 'Research' },
  { path: '/about', label: 'About' },
  { path: '/announcements', label: 'Announcements' },
];

const FOOTER_LINKS: { heading: string; links: { to: string; label: string }[] }[] = [
  {
    heading: 'Product',
    links: [
      { to: '/', label: 'Home' },
      { to: '/how-it-works', label: 'How It Works' },
    ],
  },
  {
    heading: 'Company',
    links: [
      { to: '/about', label: 'About' },
      { to: '/contact', label: 'Contact' },
      { to: '/announcements', label: 'Announcements' },
    ],
  },
  {
    heading: 'Legal',
    links: [
      { to: '/privacy', label: 'Privacy Policy' },
      { to: '/terms', label: 'Terms of Service' },
    ],
  },
];

export const LandingLayout: React.FC<LandingLayoutProps> = ({ onLogin, partner }) => {
  const location = useLocation();
  const { theme, setTheme } = useDesignSystem();
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const logoSrc = theme === 'light' ? '/career_catalyst_header_light.png' : '/career_catalyst_header.png';

  const handleGetStarted = () => {
    setShowLoginModal(true);
  };

  // Token refresh failure redirects here with this flag (services/api.ts)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('session_expired') === 'true') {
      params.delete('session_expired');
      const rest = params.toString();
      window.history.replaceState(null, '', window.location.pathname + (rest ? `?${rest}` : ''));
      setShowLoginModal(true);
      toast.info('Your session expired. Please sign in again.');
    }
  }, []);

  return (
    <div className="relative min-h-screen bg-background text-foreground">
      {/* Header */}
      <header className="sticky top-0 z-50 border-b border-border bg-background/80 backdrop-blur-sm">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <div className="relative flex h-14 items-center justify-between">
            {/* Logo - Left */}
            <div className="flex flex-1 justify-start md:flex-none">
              <Link to="/" className="group flex items-center">
                <img
                  src={logoSrc}
                  alt="Career Catalyst"
                  className="h-7 w-auto max-w-[170px] object-contain transition-transform duration-200 group-hover:scale-105 sm:h-8 sm:max-w-none"
                />
              </Link>
            </div>

            {/* Navigation - Center */}
            <nav className="absolute left-1/2 hidden -translate-x-1/2 items-center gap-1 md:flex">
              {NAV_ITEMS.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  className={cn(
                    'rounded-lg px-3 py-2 text-sm font-medium transition-colors',
                    location.pathname === item.path
                      ? 'bg-muted text-foreground'
                      : 'text-muted-foreground hover:bg-muted/50 hover:text-foreground',
                  )}
                >
                  {item.label}
                </Link>
              ))}
            </nav>

            {/* CTA - Right */}
            <div className="flex items-center gap-2">
              <Button
                size="icon"
                variant="ghost"
                className="hidden md:inline-flex"
                aria-label="Toggle theme"
                onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
              >
                {theme === 'dark' ? <Sun /> : <Moon />}
              </Button>
              <Button
                onClick={handleGetStarted}
                className={cn(
                  BRAND_GRADIENT,
                  'text-white shadow-lg shadow-primary/25 hover:shadow-primary/40',
                )}
              >
                Get Started
              </Button>
              {/* Mobile menu toggle */}
              <Button
                size="icon"
                variant="ghost"
                className="md:hidden"
                aria-label="Toggle menu"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              >
                {mobileMenuOpen ? <X /> : <Menu />}
              </Button>
            </div>
          </div>
        </div>

        {/* Expandable Mobile Menu */}
        {mobileMenuOpen && (
          <nav className="border-t border-border bg-background/95 backdrop-blur-sm md:hidden">
            <div className="flex flex-col gap-1 px-4 py-2">
              {NAV_ITEMS.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setMobileMenuOpen(false)}
                  className={cn(
                    'rounded-lg px-4 py-3 text-sm transition-colors',
                    location.pathname === item.path
                      ? 'bg-accent text-accent-foreground'
                      : 'text-muted-foreground hover:bg-muted/50 hover:text-foreground',
                  )}
                >
                  {item.label}
                </Link>
              ))}
            </div>
          </nav>
        )}
      </header>

      {/* Main Content */}
      <main className="relative z-10">
        <Outlet context={{ onGetStarted: handleGetStarted }} />
      </main>

      {/* Footer */}
      <footer className="border-t border-border py-16">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col justify-between gap-8 md:flex-row md:gap-12">
            {/* Brand */}
            <div className="max-w-xs">
              <img
                src={logoSrc}
                alt="Career Catalyst"
                className="mb-4 h-8 w-auto"
              />
              <p className="text-sm text-muted-foreground">
                AI-powered career advancement for the modern job seeker
              </p>
            </div>

            {/* Link groups */}
            <div className="flex flex-col gap-8 sm:flex-row sm:gap-12 md:gap-16">
              {FOOTER_LINKS.map((group) => (
                <div key={group.heading}>
                  <h3 className="mb-4 text-sm font-medium">{group.heading}</h3>
                  <ul className="space-y-2">
                    {group.links.map((link) => (
                      <li key={link.to}>
                        <Link
                          to={link.to}
                          className="text-sm text-muted-foreground transition-colors hover:text-foreground"
                        >
                          {link.label}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-12 border-t border-border pt-8 text-center">
            <p className="text-sm text-muted-foreground">© 2026 L L C, LLC. All rights reserved.</p>
          </div>
        </div>
      </footer>

      {/* Login/Register Dialog (shared component) */}
      <AuthDialog
        open={showLoginModal}
        onOpenChange={setShowLoginModal}
        onAuthenticated={onLogin}
        partner={partner}
      />
    </div>
  );
};
